import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { emailApi } from "../../services/endpoints";
import { unwrapList } from "../../services/response";
import type { QueryParams } from "../../types/api";
import type { StrapiEntity } from "../../types/entities";

/**
 * An email outbox row (Strapi `sendemails` entity) — the scalar set below is
 * exactly what the request's `fields[…]` allow-list asks for (plus the
 * id/documentId Strapi always injects; the createdAt/updatedAt/publishedAt
 * inherited from StrapiEntity are deliberately NOT selected), so the type and the
 * wire shape stay checkable against each other. A narrower view of the row the
 * preview tree consumes as `OutboxEmail` (stores/emailPreviewStore) — nothing
 * enforces that relationship, since the grid takes untyped rowData.
 */
interface EmailRow extends StrapiEntity {
  to?: string;
  cc?: string;
  optional_cc_email?: string;
  from?: string;
  subject?: string;
  body?: string;
  bounced?: boolean;
  email_sent?: boolean;
  time_to_send?: string;
  send_status?: string;
  /**
   * Bounce/complaint records written by the Postmark webhook; only the preview
   * modal reads inside it. Spelled out rather than `unknown` so this view and
   * `OutboxEmail`'s can be compared. `status` is wider here on purpose: the
   * webhook stores Postmark's numeric `TypeCode` (see the biteg-api sns
   * controller), which OutboxEmail currently types as string.
   */
  rejection_object?: Array<{
    emailAddress?: string;
    action?: string;
    status?: string | number;
    diagnosticCode?: string;
  }>;
  attachments?: Array<{ id: number; name?: string }>;
}

/** The arguments driving an outbox scope (mode + search + sender). */
interface EmailOutboxArgs {
  mode?: string;
  searchquery?: string | null;
  email?: string;
}

export const emailsQueryKey = ["emails"];

/**
 * Query options for the email outbox, keyed by mode + search + sender. The list
 * changes as mail is sent/bounces, so staleTime:0 keeps it always-fresh (matches
 * the previous getFutureEmails behaviour) while adding request dedup.
 */
export const emailsQueryOptions = ({
  mode = "future",
  searchquery = null,
  email,
}: EmailOutboxArgs) => ({
  queryKey: [...emailsQueryKey, { mode, searchquery, email }],
  staleTime: 0,
  queryFn: async (): Promise<EmailRow[]> => {
    const inputdate = new Date();
    inputdate.setDate(inputdate.getDate() - 90);

    const params: QueryParams =
      mode === "bounced"
        ? { "filters[bounced]": true }
        : {
            "filters[email_sent]": mode !== "future",
            ...(searchquery === null
              ? { "filters[time_to_send][$gte]": inputdate }
              : {
                  "filters[$or][0][to][$containsi]": searchquery,
                  "filters[$or][1][cc][$containsi]": searchquery,
                  "filters[$or][2][optional_cc_email][$containsi]": searchquery,
                  "filters[$or][4][body][$containsi]": searchquery,
                  "filters[$or][5][subject][$containsi]": searchquery,
                }),
            "filters[from]": email,
          };

    const res = await emailApi.getAll({
      ...params,
      // This was `populate: true`, which is a SILENT NO-OP in Strapi 5: only the
      // literal '*' expands to "everything" at depth 0. A boolean serializes to
      // the string "true", convert-query-params turns that into the path list
      // ["true"], and the populate validator waves boolean-like keys through
      // without complaint — so `attachments` never came back and the "Anhang"
      // column rendered "Nein" for every row against the real API. The e2e mocks
      // return the relation regardless of the query string, which is why nothing
      // caught it. Relations must be named explicitly here.
      //
      // `name` is all the grid/preview need (Ja/Nein, then the filename); the
      // download posts `attachment.id`, and Strapi always adds id/documentId to
      // a `fields` pick, so it comes along for free.
      "populate[attachments][fields][0]": "name",
      // Scalar allow-list, for the same reason the populate is explicit: the
      // default "all columns" ships `attachment_buffer`, a legacy text column
      // that held base64 attachment payloads until 2023. The bounced and search
      // scopes have no 90-day window, so they reach rows old enough to still
      // carry those blobs. id/documentId are injected by Strapi — DataGrid's
      // getRowId and apiId(previewEmail) both need them.
      "fields[0]": "from",
      "fields[1]": "to",
      "fields[2]": "cc",
      "fields[3]": "optional_cc_email",
      "fields[4]": "subject",
      "fields[5]": "body",
      "fields[6]": "time_to_send",
      "fields[7]": "send_status",
      "fields[8]": "rejection_object",
      "fields[9]": "bounced",
      "fields[10]": "email_sent",
      "sort[0]": "createdAt:desc",
    });
    return unwrapList<EmailRow>(res);
  },
});

/**
 * Live email-outbox query. Replaces the old getFutureEmails + AppState mirror:
 * components read the outbox straight from the cache, keyed on {mode, search,
 * email}, so a mode/search change refetches automatically. Disabled until we
 * have a sender email (mirrors the old "only fetch once authenticated").
 */
export const useEmailOutbox = ({ mode = "future", searchquery = null, email }: EmailOutboxArgs) =>
  useQuery({
    ...emailsQueryOptions({ mode, searchquery, email }),
    enabled: !!email,
  });

/**
 * Returns a stable callback that refreshes every cached outbox scope
 * (future/past/bounced/search). Call it after any email mutation (delete,
 * bulk-delete, resend) or after an action that queues mail (invoice/email
 * generation) — replaces the old imperative getFutureEmails() refresh.
 */
export const useRefreshEmails = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: emailsQueryKey }),
    [queryClient]
  );
};
